import Vue from 'vue';
import _ from 'lodash';
import Cropper from 'cropperjs';
import { mapGetters } from 'vuex';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import path from 'path';
import { siteBuilder } from '@/mixins/site-builder';

export default Vue.component('site-meta-data', {
  template: require('./template.html'),
  name: 'site-meta-data',
  mixins: [guid, siteBuilder, uploader],
  computed: {
    ...mapGetters(['currentSite', 'currentPage']),
  },
  components: {
    uploadProgress,
  },
  data() {
    return {
      loaders: {
        meta: false,
      },
      edit: {
        meta: false,
      },
      changed: {
        meta: false,
      },
      metaimage: null,
      hoveredImg: false,
      initial: {
        meta: false,
      },
      localFile: null,
      openCropper: false,
      cropper: null,
    };
  },
  created() {
    this.checkNewValues();
  },
  watch: {
    showModal(o) {
      if (o) {
        this.initial.meta = _.cloneDeep(this.currentSite.meta);
      } else {
        this.onCloseModal();
      }
    },
    openCropper(open) {
      if (open) {
        setTimeout(() => {
          const image = this.$refs.imageCropper;
          this.cropper = new Cropper(image, {
            aspectRatio: 40 / 21,
            dragMode: 'move',
            autoCropArea: 1,
            zoomable: false,
          });
        }, 300);
      }
    },
  },
  props: ['show-modal', 'toggle-modal'],
  methods: {
    checkNewValues() {
      const values = [];
      if (!this.currentSite.meta) {
        values.push({
          key: 'meta',
          value: {
            image: false,
          },
        });
      }
      if (values.length > 0) {
        this.$store.dispatch('setNewSiteValues', {
          site: this.currentSite,
          vm: this,
          values,
        });
      }
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    destroyCropper() {
      this.openCropper = false;
      this.localFile = null;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    openUpload(type) {
      this.$refs[type].click();
    },
    async uploadCropped() {
      try {
        const canvas = this.cropper.getCroppedCanvas({
          width: 1200,
          height: 630,
          imageSmoothingQuality: 'high',
        });
        const blob = await this.getBlob(canvas);
        // send off to our s3 upload mixin
        const filePath = `/_meta/${this.localFile}`;
        await this.uploadFile({
          file: `${this._sitePath}${filePath}`,
          body: blob,
        });
        const meta = this.currentSite.meta ? _.cloneDeep(this.currentSite.meta) : {};
        meta.image = filePath;
        this.$store.dispatch('setNewSiteValues', {
          site: this.currentSite,
          vm: this,
          values: [
            {
              key: 'meta',
              value: meta,
            },
          ],
        });
        this.changed.meta = true;
        this.destroyCropper();
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'metaImage uploadCropped',
          });
        }
      }
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.openCropper = base64File;
              // gather naming information
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `drz_metaimage_${_.trim(fileName)}`;
              fileName = _.replace(fileName, /\s+/g, '_');
              this.localFile = fileName;
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'metaImage openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    clearMetaImg() {
      const meta = _.cloneDeep(this.currentSite.meta);
      meta.image = false;
      this.$store.dispatch('setNewSiteValues', {
        site: this.currentSite,
        vm: this,
        values: [
          {
            key: 'meta',
            value: meta,
          },
        ],
      });
      if (!this.initial.meta) {
        this.changed.meta = false;
      } else {
        this.changed.meta = true;
      }
    },
    closeModal() {
      this.$emit('toggle-modal');
    },
    cancelItem(key) {
      if (this.changed[key]) {
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key,
          value: _.cloneDeep(this.initial[key]),
        });
        this.changed[key] = false;
      }
    },
    async saveItem(key) {
      if (this.changed[key]) {
        this.loaders[key] = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Successfully saved meta image!',
        };
        try {
          const functions = [
            {
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'meta',
                value: this.currentSite.meta,
              },
            },
          ];
          await this._SEND(functions);
          this.$log.debug('successfully saved site changes');
          // since this is a site wide change, we need to regenerate the
          // site and push it to s3
          await this.callBuilder({
            publish: 'site',
            site: {
              _id: this.currentSite._id,
            },
          });
        } catch (err) {
          this.$log.error(err);
          notif.type = 'error';
          notif.message = err.message || err;
          await this._reportError({
            error: err,
            fn: `save ${key} settings`,
          });
        } finally {
          this.loaders[key] = false;
          this.initial[key] = _.cloneDeep(this.currentSite[key]);
          this.changed[key] = false;
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    onCloseModal() {
      _.forOwn(this.changed, (changed, key) => {
        // if something was changed but not saved on close,
        // we need to revert it back
        if (changed) {
          this.cancelItem(key);
        }
      });
    },
    formatErrors(ers) {
      let string = '';
      _.forEach(ers, (er) => {
        string = string += ` ${er}`;
      });
      return string;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/global-options/meta/index.js